import React from "react"
import { useTranslation } from "react-i18next"

import { CopyIconButton } from "@/components/copy-icon-button"
import { Button } from "@/nzds"
import { HStack } from "@/nzds/layout"
import { useDisclosure } from "@/nzds/utils"
import { useGtag } from "@/services/gtag"

import CloseIcon from "@mui/icons-material/Close"
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined"
import {
  IconButton,
  Popover,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"

import type { ShareButtonProps } from "./share-button.types"

export const ShareButton: React.FC<ShareButtonProps> = ({ iconButton }) => {
  const { t } = useTranslation()
  const { gtagData } = useGtag()
  const { close, isOpen, open } = useDisclosure()
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null)
  const title = t("share.title", { ns: "common" })

  const currentHref = window.location.href

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    open()
    setAnchorEl(event.currentTarget)
  }

  return (
    <>
      {iconButton ? (
        <Tooltip title={title}>
          <IconButton aria-label={title} color="neutral" onClick={handleOpen}>
            <ShareOutlinedIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          color="neutral"
          onClick={open}
          startIcon={<ShareOutlinedIcon />}
          variant="text"
        >
          {title}
        </Button>
      )}
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        onClose={close}
        open={isOpen}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Stack
          sx={{
            gap: 1,
            p: 3,
            width: (theme) => theme.typography.pxToRem(484),
          }}
        >
          <HStack sx={{ justifyContent: "space-between" }}>
            <Typography variant="h2">{title}</Typography>
            <IconButton
              aria-label={t("button.close", { ns: "common" })}
              onClick={close}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </HStack>
          <HStack sx={{ alignItems: "center", gap: 2 }}>
            <TextField
              aria-label={t("share.urlLabel", { ns: "common" })}
              fullWidth
              slotProps={{
                htmlInput: {
                  readOnly: true,
                },
              }}
              value={currentHref}
            />
            <CopyIconButton
              ariaLabel={t("share.copyIcon", { ns: "common" })}
              color="neutral"
              onCopy={(value) => {
                gtagData?.gtag("event", "share", {
                  content_type: "url",
                  item_id: value,
                })
              }}
              successMessage={t("share.copySuccess", { ns: "common" })}
              value={currentHref}
            />
          </HStack>
        </Stack>
      </Popover>
    </>
  )
}
